/**
 * Palto Cards: "magnetic" hover for product tiles and the homepage catalog
 * tiles (Pokémon / Magic / One Piece / Yu-Gi-Oh!) - see snippets/palto-motion.liquid.
 *
 * While the mouse is over a tile, the whole tile lifts 4px and drifts up to
 * 4px (product tiles) or 3px (catalog tiles) per axis AWAY from the pointer,
 * easing after it each frame; on leave it eases back to rest. One set of document-level listeners covers
 * every tile, including ones rendered later (carousels, filtering, infinite
 * loading, recommendations), so nothing is bound per tile.
 *
 * - Only for a real mouse on wide screens, never with reduced motion.
 * - Moves the tile with the `translate` property (no layout, no reflow).
 *   In carousels the slide is moved instead of the card, because each slide
 *   clips its card (overflow: hidden); the carousel track has enough inner
 *   padding for the few pixels of movement.
 * - Hover is decided against the tile's resting box, so a tile drifting away
 *   from the pointer can never flicker in and out of hover at its edges.
 * - The tile never moves so far that the spot under the cursor stops being
 *   part of it (clampToPointer). Without this, a pointer near a tile's edge
 *   ended up beside the moved tile and a click there hit the page behind it
 *   and was lost (fix 2026-10-16: ~6% of each product tile, 7.5% of each
 *   catalog tile, mostly along the bottom edge).
 * - Pressing a mouse button (e.g. dragging a carousel) releases the tile.
 */

const MAX_SHIFT = 4; // px per axis, away from the pointer (product tiles)
const CATALOG_MAX_SHIFT = 3; // px per axis for the catalog tiles
// Only active (linked) catalog tiles - "Coming soon" tiles get no hover effect.
const CATALOG_TILE = 'main .carousel-content slideshow-slides > .card:has(> .card__link)';
const LIFT = 4; // px upwards while hovered
const FOLLOW = 0.14; // easing factor per 60fps frame (lower = lazier), scaled by real frame time
const REST_EPSILON = 0.05;

const media = window.matchMedia(
  '(hover: hover) and (pointer: fine) and (min-width: 750px) and (prefers-reduced-motion: no-preference)'
);

/** @typedef {{ el: HTMLElement, x: number, y: number, maxShift: number, base: DOMRect | null }} TileState */

/** @type {Map<HTMLElement, TileState>} */
const moving = new Map();
/** @type {TileState | null} */
let active = null;
let pointerX = 0;
let pointerY = 0;
let frame = 0;
let lastTime = 0;
let baseDirty = false;

/** @param {HTMLElement} card */
function tileOf(card) {
  return /** @type {HTMLElement} */ (card.closest('.resource-list__slide') || card);
}

/** @param {TileState} state */
function measureBase(state) {
  const rect = state.el.getBoundingClientRect();
  state.base = new DOMRect(rect.left - state.x, rect.top - state.y, rect.width, rect.height);
}

/**
 * Limits the active tile's offset so the pointer stays at least 1px inside
 * the moved tile - every click on a hovered tile reaches the tile.
 * @param {TileState} state
 */
function clampToPointer(state) {
  const base = state.base;
  if (!base) return;
  state.x = Math.min(Math.max(state.x, pointerX - base.right + 1), pointerX - base.left - 1);
  state.y = Math.min(Math.max(state.y, pointerY - base.bottom + 1), pointerY - base.top - 1);
}

/** @param {TileState} state */
function pointerInside(state) {
  const base = state.base;
  if (!base) return false;
  return pointerX >= base.left && pointerX <= base.right && pointerY >= base.top && pointerY <= base.bottom;
}

/**
 * @param {HTMLElement} el
 * @param {number} maxShift
 */
function activate(el, maxShift) {
  if (active && active.el === el) return;
  release();
  let state = moving.get(el);
  if (!state) {
    state = { el, x: 0, y: 0, maxShift, base: null };
    moving.set(el, state);
  }
  measureBase(state);
  el.classList.add('palto-magnetic', 'palto-magnetic--active');
  active = state;
  schedule();
}

function release() {
  if (!active) return;
  active.el.classList.remove('palto-magnetic--active');
  active = null;
  schedule();
}

function schedule() {
  if (!frame) frame = requestAnimationFrame(tick);
}

/** @param {number} now */
function tick(now) {
  frame = 0;
  const elapsed = lastTime ? Math.min(now - lastTime, 100) : 1000 / 60;
  lastTime = now;
  const follow = 1 - Math.pow(1 - FOLLOW, elapsed / (1000 / 60));

  if (active && baseDirty) {
    measureBase(active);
    if (!pointerInside(active)) release();
  }
  baseDirty = false;

  for (const state of moving.values()) {
    let targetX = 0;
    let targetY = 0;

    if (state === active && state.base) {
      const base = state.base;
      const nx = Math.max(-1, Math.min(1, (pointerX - (base.left + base.width / 2)) / (base.width / 2)));
      const ny = Math.max(-1, Math.min(1, (pointerY - (base.top + base.height / 2)) / (base.height / 2)));
      targetX = -nx * state.maxShift;
      targetY = -ny * state.maxShift - LIFT;
    }

    state.x += (targetX - state.x) * follow;
    state.y += (targetY - state.y) * follow;
    if (state === active) clampToPointer(state);

    if (state !== active && Math.abs(state.x) < REST_EPSILON && Math.abs(state.y) < REST_EPSILON) {
      state.el.style.removeProperty('translate');
      state.el.classList.remove('palto-magnetic');
      moving.delete(state.el);
      continue;
    }

    state.el.style.translate = `${state.x.toFixed(2)}px ${state.y.toFixed(2)}px`;
  }

  if (moving.size) {
    schedule();
  } else {
    lastTime = 0;
  }
}

/** @param {PointerEvent} event */
function onPointerMove(event) {
  if (event.pointerType !== 'mouse' || !media.matches) return;

  pointerX = event.clientX;
  pointerY = event.clientY;

  if (event.buttons) {
    release();
    return;
  }

  const target = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
  const card = /** @type {HTMLElement | null} */ (target?.closest('main product-card') ?? null);
  const catalogTile = card ? null : /** @type {HTMLElement | null} */ (target?.closest(CATALOG_TILE) ?? null);

  if (card) {
    activate(tileOf(card), MAX_SHIFT);
  } else if (catalogTile) {
    activate(catalogTile, CATALOG_MAX_SHIFT);
  } else if (active && !pointerInside(active)) {
    release();
  }

  if (active) {
    // Re-clamp right away, not on the next frame, so the tile is under the pointer before any click.
    clampToPointer(active);
    active.el.style.translate = `${active.x.toFixed(2)}px ${active.y.toFixed(2)}px`;
    schedule();
  }
}

function onScroll() {
  if (!active) return;
  baseDirty = true;
  schedule();
}

function releaseAll() {
  release();
  for (const state of moving.values()) {
    state.el.style.removeProperty('translate');
    state.el.classList.remove('palto-magnetic', 'palto-magnetic--active');
  }
  moving.clear();
}

if (!window.paltoMotionReady) {
  window.paltoMotionReady = true;

  document.addEventListener('pointermove', onPointerMove, { passive: true });
  document.addEventListener('pointerdown', release, { passive: true });
  document.addEventListener('scroll', onScroll, { passive: true, capture: true });
  document.documentElement.addEventListener('pointerleave', release, { passive: true });
  window.addEventListener('blur', release);
  window.addEventListener('pagehide', releaseAll);
  media.addEventListener('change', () => {
    if (!media.matches) releaseAll();
  });
}
